import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, Trash2, ExternalLink } from "lucide-react";
import ImagePicker from "@/components/ImagePicker";

export default function LandingsPage() {
  const [landings, setLandings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    slug: "", title: "", headline: "", subheadline: "", hero_image: "",
    bullets: "", cta_text: "Solicita tu itinerario",
  });

  const load = () => api.get("/landings").then((r) => setLandings(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.slug || !form.title) return toast.error("Slug y título obligatorios");
    try {
      await api.post("/landings", {
        ...form,
        bullets: form.bullets.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Landing creada");
      setOpen(false);
      setForm({ slug: "", title: "", headline: "", subheadline: "", hero_image: "", bullets: "", cta_text: "Solicita tu itinerario" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    await api.delete(`/landings/${id}`);
    toast.success("Landing eliminada");
    load();
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/l/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copiada al portapapeles");
  };

  return (
    <div className="stagger">
      <PageHeader
        overline="Landing Pages"
        title="Páginas de captura."
        subtitle="Crea landings públicas para tus campañas. Cada formulario alimenta automáticamente tu CRM."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="landing-create-button" className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
                <Plus className="w-4 h-4 mr-2" />Nueva landing
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-sm max-w-2xl">
              <DialogHeader><DialogTitle className="font-display">Nueva landing</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Slug (URL)</Label><Input data-testid="landing-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="bali-2026" className="mt-1 rounded-sm" /></div>
                  <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" /></div>
                </div>
                <div><Label>Headline</Label><Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="mt-1 rounded-sm" /></div>
                <div><Label>Subheadline</Label><Textarea value={form.subheadline} onChange={(e) => setForm({ ...form, subheadline: e.target.value })} className="mt-1 rounded-sm" /></div>
                <div><Label>URL imagen hero</Label><Input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} placeholder="https://..." className="mt-1 rounded-sm" /></div>
                <ImagePicker
                  label="...o elige de tu biblioteca"
                  value={form.hero_image}
                  onChange={(url) => setForm({ ...form, hero_image: url })}
                  testid="landing-hero-picker"
                />
                <div><Label>Bullets (uno por línea)</Label><Textarea value={form.bullets} onChange={(e) => setForm({ ...form, bullets: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" rows={4} /></div>
                <div><Label>Texto del CTA</Label><Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="mt-1 rounded-sm" /></div>
              </div>
              <DialogFooter><Button data-testid="landing-save" onClick={create} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">Crear</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid md:grid-cols-2 gap-5">
        {landings.length === 0 && (
          <Card className="edit-card p-12 rounded-sm shadow-none text-center text-[#5C616A] md:col-span-2">
            Aún no hay landings. Crea la primera para comenzar a captar leads.
          </Card>
        )}
        {landings.map((l) => (
          <Card key={l.id} className="edit-card overflow-hidden rounded-sm shadow-none">
            {l.hero_image && (
              <img src={l.hero_image} alt={l.title} className="w-full h-32 object-cover" />
            )}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="rounded-sm text-[10px] font-mono">/{l.slug}</Badge>
                <Badge variant="outline" className="rounded-sm text-[10px] border-[#2D6A4F] text-[#2D6A4F]">{l.submissions} leads</Badge>
                <Badge variant="outline" className="rounded-sm text-[10px]">{l.visits} visitas</Badge>
              </div>
              <h3 className="font-display text-xl text-[#0A1128]">{l.title}</h3>
              <p className="text-sm text-[#5C616A] mt-1 line-clamp-2">{l.headline}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`/l/${l.slug}`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="rounded-sm border-[#E2DCD0]" data-testid={`landing-view-${l.id}`}>
                    <ExternalLink className="w-3 h-3 mr-1" />Ver
                  </Button>
                </a>
                <Button size="sm" variant="outline" onClick={() => copyUrl(l.slug)} className="rounded-sm border-[#E2DCD0]">
                  <Copy className="w-3 h-3 mr-1" />URL
                </Button>
                <Button size="sm" variant="outline" onClick={() => remove(l.id)} className="rounded-sm border-[#E2DCD0] hover:border-[#9D0208] hover:text-[#9D0208]">
                  <Trash2 className="w-3 h-3 mr-1" />Eliminar
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
